import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeToggle } from "./ThemeToggle";
import { LangToggle } from "./LangToggle";
import { KikiLogo } from "./KikiLogo";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

export function Header({ onOpenEnquiry }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const t = useT();
  const { lang } = useLang();

  const links = [
    { href: "#programmes", label: t("nav.programmes"), id: KIKI.navProgrammes },
    { href: "#manifesto", label: t("nav.whyKiki"), id: KIKI.navManifesto },
    { href: "#mentors", label: t("nav.mentors"), id: KIKI.navMentors },
    { href: "#outcomes", label: t("nav.outcomes"), id: KIKI.navOutcomes },
    { href: "#fees", label: t("nav.fees"), id: KIKI.navFees },
    { href: "#faq", label: t("nav.faq"), id: KIKI.navFaq },
    { href: "#contact", label: t("nav.contact"), id: KIKI.navContact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid={KIKI.header}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-background/70 border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[90rem] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid={KIKI.logo} className="flex items-center gap-2.5">
          <span className="inline-flex items-center bg-[#FFCC00] rounded-lg px-2.5 py-1.5 md:px-3 md:py-2">
            <KikiLogo className="h-[36.4px] md:h-9 w-auto" />
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className={`text-[13px] font-medium text-muted-foreground hover:text-foreground link-sweep transition-colors ${lang === "hi" ? "font-hindi" : ""}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <LangToggle />
          <ThemeToggle />
          <button
            onClick={onOpenEnquiry}
            data-testid={KIKI.ctaEnquire}
            className={`btn-pill-yellow hidden md:inline-flex ${lang === "hi" ? "font-hindi" : ""}`}
          >
            {t("nav.enquire")}
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={t("nav.menu")}
            className="lg:hidden h-10 w-10 grid place-items-center border border-border"
          >
            <div className="space-y-1.5">
              <div className={`h-px w-4 bg-foreground transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
              <div className={`h-px w-4 bg-foreground transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
            </div>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-border bg-background/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className={`font-mono text-[12px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground ${lang === "hi" ? "font-hindi tracking-normal text-[14px] normal-case" : ""}`}
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { setOpen(false); onOpenEnquiry(); }}
                className={`btn-pill-yellow mt-2 w-full ${lang === "hi" ? "font-hindi" : ""}`}
              >
                {t("nav.enquire")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
